import React from 'react';

// Helper function to determine shape type
const getShapeType = (points) => {
  if (!points || points.length < 2) return "unknown";
  
  // Calculate bounds
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  
  points.forEach(point => {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  });
  
  const width = maxX - minX;
  const height = maxY - minY;
  
  // Classify shape
  if (height < 10 && width > 30) return "horizontal_line";
  if (width < 10 && height > 30) return "vertical_line";
  if (Math.abs(width - height) < 20 && width > 20 && height > 20) return "circle";
  if (width > 20 && height > 20 && Math.abs(width - height) >= 20) return "rectangle";
  
  return "freeform";
};

// Get shape label based on type
const getShapeLabel = (shapeType) => {
  switch(shapeType) {
    case "horizontal_line": return "Horizontal Line";
    case "vertical_line": return "Vertical Line";
    case "circle": return "Circle";
    case "rectangle": return "Rectangle";
    case "freeform": return "Custom Shape";
    default: return "Shape";
  }
};

function AnnotationLayer({ 
  annotations, 
  drawingMode = 'select', 
  scale = 1, 
  offset = { x: 0, y: 0 },
  selectedDrawings = [] // Add prop for selected drawings
}) {
  if (!annotations || annotations.length === 0) {
    return null; // Don't render anything if there are no annotations
  }

  return (
    <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
      {drawingMode === 'select' && annotations.map((ann, index) => {
        if (ann.points) {
          // This is a drawing in select mode
          // Create a path string from the points array
          const pathData = ann.points.reduce((path, point, i) => {
            // First point starts with M (move to), the rest use L (line to)
            return path + (i === 0 ? `M ${point.x},${point.y}` : ` L ${point.x},${point.y}`);
          }, '');

          const isSelected = selectedDrawings.includes(ann.id);
          
          // Get shape info if selected
          let shapeType = null;
          let bounds = null;
          let dimensions = null;
          
          if (isSelected) {
            shapeType = getShapeType(ann.points);
            
            // Calculate bounds
            let minX = ann.points[0].x;
            let minY = ann.points[0].y;
            let maxX = ann.points[0].x;
            let maxY = ann.points[0].y;
            
            ann.points.forEach(point => {
              minX = Math.min(minX, point.x);
              minY = Math.min(minY, point.y);
              maxX = Math.max(maxX, point.x);
              maxY = Math.max(maxY, point.y);
            });
            
            bounds = { minX, minY, maxX, maxY };
            dimensions = {
              width: maxX - minX,
              height: maxY - minY
            };
          }
          
          return (
            <g key={index}>
              <path
                d={pathData}
                fill="none"
                stroke={isSelected ? "#4CAF50" : "#FF3366"} // Green when selected, pink otherwise
                strokeWidth={isSelected ? "4" : "3"}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {isSelected && bounds && (
                <>
                  {/* Bounding box for selected drawings */}
                  <rect
                    x={bounds.minX - 2}
                    y={bounds.minY - 2}
                    width={dimensions.width + 4}
                    height={dimensions.height + 4}
                    fill="none"
                    stroke="#4CAF50"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  
                  {/* Show shape type and dimensions */}
                  <rect
                    x={bounds.minX - 2}
                    y={bounds.minY - 30}
                    width={Math.max(150, dimensions.width + 4)}
                    height="28"
                    fill="rgba(74, 175, 80, 0.8)"
                    rx="4"
                  />
                  <text
                    x={bounds.minX + 5}
                    y={bounds.minY - 12}
                    fill="white"
                    fontSize="12"
                    fontFamily="sans-serif"
                  >
                    {getShapeLabel(shapeType)} ({Math.round(dimensions.width)}×{Math.round(dimensions.height)})
                  </text>
                  
                  {/* Shape guide based on the shape type */}
                  {shapeType === "rectangle" && (
                    <rect
                      x={bounds.minX}
                      y={bounds.minY}
                      width={dimensions.width}
                      height={dimensions.height}
                      fill="rgba(74, 175, 80, 0.1)"
                      stroke="#4CAF50"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                  )}
                  {shapeType === "circle" && (
                    <ellipse
                      cx={(bounds.minX + bounds.maxX) / 2}
                      cy={(bounds.minY + bounds.maxY) / 2}
                      rx={dimensions.width / 2}
                      ry={dimensions.height / 2}
                      fill="rgba(74, 175, 80, 0.1)"
                      stroke="#4CAF50"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                  )}
                  {shapeType === "horizontal_line" && (
                    <line
                      x1={bounds.minX}
                      y1={(bounds.minY + bounds.maxY) / 2}
                      x2={bounds.maxX}
                      y2={(bounds.minY + bounds.maxY) / 2}
                      stroke="#4CAF50"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                  )}
                  {shapeType === "vertical_line" && (
                    <line
                      x1={(bounds.minX + bounds.maxX) / 2}
                      y1={bounds.minY}
                      x2={(bounds.minX + bounds.maxX) / 2}
                      y2={bounds.maxY}
                      stroke="#4CAF50"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                  )}
                </>
              )}
            </g>
          );
        } else {
          // This is a regular selection box
          return (
            <rect
              key={index}
              x={ann.x}
              y={ann.y}
              width={ann.width}
              height={ann.height}
              fill="none"
              stroke="#FF3366" // Annotation color
              strokeWidth="2"
              strokeDasharray="5,5" // Dashed line style
            />
          );
        }
      })}
      
      {drawingMode === 'draw' && annotations.map((stroke, index) => {
        if (!stroke.points || stroke.points.length < 2) return null;
        
        // Create a path string from the points array
        const pathData = stroke.points.reduce((path, point, i) => {
          // First point starts with M (move to), the rest use L (line to)
          return path + (i === 0 ? `M ${point.x},${point.y}` : ` L ${point.x},${point.y}`);
        }, '');
        
        return (
          <path
            key={index}
            d={pathData}
            fill="none"
            stroke="#FF3366" // Drawing color
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
    </svg>
  );
}

export default AnnotationLayer; 